"use client";
import React from "react";
import { createPortal } from "react-dom";
import CustomTooltip from "../shared/CustomTooltip";
import { useEffect, useState, useMemo } from "react";
import {
  ArrowLeft,
  Users,
  User,
  Plus,
  Minus,
  Search,
  TrendingUp,
  Award,
  Settings,
  UserPlus,
  Filter,
  SortAsc,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Info,
  X,
  ExternalLink,
  BookOpen,
  Briefcase,
  Download,
  Target,
  Lightbulb,
  ArrowRight,
  AlertCircle,
  Star,
  PlayCircle,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./TeamDetailPage.css";
import ProfileCard from "../shared/profileCard";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getTeamByIdApi,
  addToTeamApi,
  removeFromTeamApi,
} from "../../services/teamsService";
import { getAllPeopleApi } from "../../services/peopleService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { getDepartmentsListApi } from "../../services/settingsService";
import { useTranslation } from "react-i18next";
import NoDataFound from "../shared/NoDataFound";
import ViewMoreModal from "../shared/ViewMoreModal";

// Generic Modal for displaying skill lists
const SkillListModal = ({ title, skills, type, onClose, onRemoveSkill }) => {
  return (
    <div className="job-skills-modal-overlay">
      <div className="job-skills-modal-content unique-job-skills-modal">
        <div className="job-skills-modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="job-skills-modal-close-btn">
            <X size={20} />
          </button>
        </div>
        <div className="job-skills-modal-grid">
          {type === "soft"
            ? skills.map((skill, index) => (
                <div key={index} className="soft-skill-item-modal">
                  <span className="soft-skill-name">{skill.name}:</span>
                  {skill.value !== undefined && (
                    <span className="soft-skill-value">{skill.value}</span>
                  )}
                </div>
              ))
            : skills.map((skill, index) => (
                <div key={index} className="job-skill-card">
                  <span>{skill}</span>
                  {onRemoveSkill && (
                    <button
                      onClick={() => onRemoveSkill(skill)}
                      className="job-skill-remove-btn"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

const FloatingHeaderTooltip = ({ tooltip, children, id }) => {
  const triggerRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState(null);

  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = Math.min(280, viewportWidth - 24);

    const rawLeft = rect.left + rect.width / 2;
    const safeLeft = Math.min(
      Math.max(rawLeft, tooltipWidth / 2 + 12),
      viewportWidth - tooltipWidth / 2 - 12,
    );

    const hasSpaceAbove = rect.top > 110;

    setCoords({
      left: safeLeft,
      top: hasSpaceAbove ? rect.top - 8 : rect.bottom + 8,
      width: tooltipWidth,
      placement: hasSpaceAbove ? "top" : "bottom",
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="tm-floating-tooltip-trigger"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>

      {open &&
        coords &&
        createPortal(
          <div
            id={id}
            className={`tm-floating-tooltip-box ${coords.placement}`}
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
            }}
            role="tooltip"
          >
            {tooltip?.title && (
              <div className="tm-floating-tooltip-title">{tooltip.title}</div>
            )}
            {tooltip?.message && (
              <div className="tm-floating-tooltip-message">
                {tooltip.message}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

const TeamDetailPage = () => {
  const { t } = useTranslation();
  const paramId = useParams().id; //userId of the employee profile
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("info");

  const tabParam = (searchParams.get("tab") || "").toLowerCase();

  useEffect(() => {
    if (tabParam === "members") {
      setActiveTab("candidates"); // ✅ members tab
    } else if (tabParam === "skills") {
      setActiveTab("skills");
    } else if (tabParam === "info") {
      setActiveTab("info");
    }
  }, [tabParam]);

  useEffect(() => {
    if (tabParam === "members" && activeTab === "candidates") {
      setTimeout(() => {
        document.getElementById("add-members-tab")?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, 50);
    }
  }, [tabParam, activeTab]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [filterAvailability, setFilterAvailability] = useState("All");
  const [employees, setEmployees] = useState([]);
  const [employeesLength, setEmployeesLength] = useState(null);
  // Pagination for candidates
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(21); // 20 items per page // Pagination
  const totalPages = Math.ceil(employeesLength / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const [sortBy, setSortBy] = useState("Name");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    action: null,
    showLocalButton: true,
  });
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const [teamData, setTeamData] = useState([]);

  // New states for soft and job skills
  const [editingSoftSkill, setEditingSoftSkill] = useState(null); // { skillName: string, originalValue: number }
  const [softSkillValues, setSoftSkillValues] = useState({}); // To hold editable soft skill values

  // Modals for skill sections
  const [showSoftSkillListModal, setShowSoftSkillListModal] = useState({
    visible: false,
    title: "",
    skills: [],
    type: "soft",
  });
  const [showJobSkillListModal, setShowJobSkillListModal] = useState({
    visible: false,
    title: "",
    skills: [],
    type: "job",
    removable: false,
  });

  const departments = [
    t("common.all"),
    "Engineering",
    "Design",
    "Product",
    "Analytics",
    "Marketing",
  ];
  const availabilityOptions = [
    t("common.all"),
    t("teamDetailPage.availability.available"),
    t("teamDetailPage.availability.partiallyAvailable"),
    t("teamDetailPage.availability.notAvailable"),
  ];
  const sortOptions = [
    { value: "Name", label: t("common.name") },
    { value: "Performance", label: t("teamDetailPage.performance") },
    { value: "Department", label: t("common.department") },
  ];

  const [vmOpen, setVmOpen] = React.useState(false);
  const [vmTitle, setVmTitle] = React.useState("");
  const [vmSkills, setVmSkills] = React.useState([]);

  // const openViewMore = (title, skills) => {
  //   setVmTitle(title);
  //   setVmSkills(skills || []);
  //   setVmOpen(true);
  // };

  const getSkillGapLabel = (gap) => {
    if (typeof gap === "string") {
      return gap.trim() || "Unnamed skill gap";
    }

    if (!gap || typeof gap !== "object") {
      return "Unnamed skill gap";
    }

    if (typeof gap.skill_gap === "string" && gap.skill_gap.trim().length > 0) {
      return gap.skill_gap.trim();
    }

    if (typeof gap.name === "string" && gap.name.trim().length > 0) {
      return gap.name.trim();
    }

    if (typeof gap.skillName === "string" && gap.skillName.trim().length > 0) {
      return gap.skillName.trim();
    }

    if (typeof gap.type === "string" && gap.type.trim().length > 0) {
      return `${gap.type.trim()} development`;
    }

    return "Unnamed skill gap";
  };

  const openViewMore = (title, skills) => {
    setVmTitle(title);

    const normalizedSkills = (Array.isArray(skills) ? skills : [])
      .map((skill) => {
        if (typeof skill === "string") {
          return skill;
        }

        if (skill && typeof skill === "object") {
          return (
            skill.name ||
            skill.skillName ||
            skill.skill_gap ||
            (skill.type ? `${skill.type} development` : "Unnamed skill")
          );
        }

        return null;
      })
      .filter(Boolean);

    setVmSkills(normalizedSkills);
    setVmOpen(true);
  };

  const Explainability = ({ text }) => {
    if (!text) return null;

    const normalized = text.replace(/\r\n/g, "\n").trim();
    const lines = normalized.split("\n").filter(Boolean);

    const heading = lines[0]; // first line is the sentence
    const bullets = lines
      .slice(1)
      .map((l) => l.replace(/^•\s*/, "").trim())
      .filter(Boolean);

    return (
      <>
        <p className="exp-heading">{heading}</p>
        {bullets.length > 0 && (
          <ul className="exp-list">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const decideTooltipSide = (e) => {
    const wrapper = e.currentTarget.closest(".mobility-tooltip");
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const tipWidth = 280; // keep in sync with CSS width
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    // If there's not enough space to the right, and more space on the left, flip it
    wrapper.dataset.side =
      spaceRight < tipWidth && spaceLeft > spaceRight ? "left" : "right";
  };

  const generateExplainabilityText = (explainability) => {
    if (!explainability || typeof explainability !== "string") return "";

    // normalize CRLF -> LF
    const str = explainability.trim().replace(/\r\n/g, "\n");

    // insert a newline before any "•" that's not already at the start of a line
    const withBulletBreaks = str.replace(/([^\n])\s*•/g, "$1\n•");

    // avoid accidental double newlines before bullets
    return withBulletBreaks.replace(/\n{2,}•/g, "\n•");
  };
  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const sortCandidates = (candidates) => {
    return [...candidates].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "role":
          return a.role.localeCompare(b.role);
        case "fit":
          return b.fit - a.fit;
        case "experience":
          return Number.parseInt(b.experience) - Number.parseInt(a.experience);
        default:
          return 0;
      }
    });
  };

  const filteredCandidates = availableCandidates.filter((candidate) => {
    const matchesSearch =
      candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.skills.some((skill) =>
        skill.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    const matchesDepartment =
      filterDepartment === t("common.all") ||
      candidate.department === filterDepartment;
    const matchesAvailability =
      filterAvailability === t("common.all") ||
      candidate.availability === filterAvailability;

    return matchesSearch && matchesDepartment && matchesAvailability;
  });

  const sortedCandidates = sortCandidates(filteredCandidates);

  const addMemberToTeamApiHandler = async (candidate) => {
    console.log("Adding member to team:", candidate);
    try {
      const userDetails = [
        {
          userId: candidate._id,
          name: candidate.name,
        },
      ];
      const obj = {
        userDetails: userDetails,
        teamId: paramId,
      };
      console.log("Adding to team with data:", obj);
      const response = await addToTeamApi(obj);
      if (response.code === 200) {
        getTeamByIdApiHandler(paramId);
        setShowModal({
          show: true,
          type: "success",
          title: t("teamDetailPage.profileAddedSuccess"),
          message: t("teamDetailPage.profileAddedMessage", {
            name: candidate.name,
          }),
          showButton: false,
          action: null,
          showLocalButton: false,
        });
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const handleAddMember = (candidate) => {
    console.log("Adding member:", candidate);
    console.log("Adding team:", teamData);

    // await getTeamByIdApiHandler(paramId);

    setShowModal({
      show: true,
      type: "info",
      title: t("teamDetailPage.addTeamMember"),
      message: t("teamDetailPage.addMemberConfirm", {
        name: candidate.name,
        team: teamData.teamName,
      }),
      showLocalButton: true,
      action: () => {
        addMemberToTeamApiHandler(candidate);
      },
    });
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message ||
          "Please upgrade your plan to continue using this feature.",
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const removeMemberFromTeamApiHandler = async (member) => {
    console.log("Removing member from team:", member);
    try {
      const userDetails = [
        {
          userId: member._id,
          name: member.name,
        },
      ];
      const obj = {
        userDetails: userDetails,
        teamId: paramId,
      };
      console.log("Removing from team with data:", obj);
      const response = await removeFromTeamApi(obj);
      if (response.code === 200) {
        getTeamByIdApiHandler(paramId);
        setShowModal({
          show: true,
          type: "success",
          title: t("teamDetailPage.memberRemovedSuccess"),
          message: t("teamDetailPage.memberRemovedMessage", {
            name: member.name,
          }),
          showButton: false,
          action: null,
          showLocalButton: false,
        });
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const handleRemoveMember = async (member) => {
    await getTeamByIdApiHandler(paramId);
    setShowModal({
      show: true,
      type: "warning",
      title: t("teamDetailPage.removeTeamMember"),
      // message: t("teamDetailPage.removeMemberConfirm", {
      //   name: member.name,
      //   team: teamData.name,
      // }),
      message: `Are you sure you want to remove ${member.name} from ${teamData.teamName} team ?`,
      showLocalButton: true,
      action: () => {
        removeMemberFromTeamApiHandler(member);
      },
    });
  };

  const handlePersonClick = (person) => {
    // console.log(person, "<person>");
    // onNavigate("/employees/id", person.userId);
    onNavigate(`/employees/${person._id}`);
  };

  const handleEditTeam = () => {
    navigate(`/teams/edit/${paramId}`);
  };
  const getPerformanceColor = (performance) => {
    if (performance >= 90) return "performance-excellent";
    if (performance >= 80) return "performance-excellent";
    return "performance-average";
  };

  const getFitColor = (fit) => {
    if (fit >= 90) return "fit-excellent";
    if (fit >= 80) return "fit-excellent";
    return "fit-average";
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case "Available":
        return "availability-available";
      case "Partially Available":
        return "availability-partial";
      default:
        return "availability-unavailable";
    }
  };

  useEffect(() => {
    getTeamByIdApiHandler(paramId);
    getDepartmentsListApiHandler();
  }, []);

  useEffect(() => {
    setEmployeesLength(employees.length || null);
    // if the list shrinks (e.g., after filter), clamp the page
    const newTotalPages = Math.max(
      1,
      Math.ceil(employees.length / itemsPerPage),
    );
    setCurrentPage((p) => Math.min(p, newTotalPages));
  }, [employees, itemsPerPage]);

  // slice for the current page
  const paginatedEmployees = useMemo(
    () => employees.slice(indexOfFirstItem, indexOfLastItem),
    [employees, indexOfFirstItem, indexOfLastItem],
  );

  // Initialize softSkillValues from ALL soft-skill sources (objects)
  useEffect(() => {
    const merged = {
      ...(teamData?.softSkills?.benchmarkSkills || {}),
      ...(teamData?.softSkills?.skillsFound || {}),
      ...(teamData?.softSkills?.skillsMissing || {}),
    };
    setSoftSkillValues(merged);
  }, [teamData]);

  // Handlers for soft skills
  const handleSoftSkillValueChange = (skillName, value) => {
    const num = Number.parseFloat(value);
    setSoftSkillValues((prev) => ({
      ...prev,
      [skillName]: Number.isNaN(num) ? 0 : num,
    }));
  };

  const handleEditSoftSkill = (skillName, value) => {
    setEditingSoftSkill({ skillName, originalValue: value });
  };

  const handleSaveSoftSkill = () => {
    setEditingSoftSkill(null);
    // In a real app, you'd send softSkillValues to your backend here
    console.log("Soft skill values saved:", softSkillValues);
  };

  const handleCancelEditSoftSkill = () => {
    if (editingSoftSkill) {
      setSoftSkillValues((prev) => ({
        ...prev,
        [editingSoftSkill.skillName]: editingSoftSkill.originalValue,
      }));
    }
    setEditingSoftSkill(null);
  };

  // Handlers for job skills
  const handleRemoveJobSkill = (skillToRemove) => {
    const updatedSkills = teamData.hardSkills.skillsFound.filter(
      (skill) => skill !== skillToRemove,
    );
    setTeamData((prev) => ({
      ...prev,
      hardSkills: {
        ...prev.hardSkills,
        skillsFound: updatedSkills,
      },
    }));
    // Close modal if the skill was removed from the modal list
    if (showJobSkillListModal.visible && showJobSkillListModal.removable) {
      setShowJobSkillListModal((prev) => ({
        ...prev,
        skills: updatedSkills,
      }));
    }
    // In a real app, you'd send this update to your backend
    console.log("Job skill removed:", skillToRemove);
  };

  const [departmentsList, setDepartmentsList] = useState([]);
  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      setDepartmentsList([
        { name: t("common.all"), _id: "all" },
        ...response.data,
      ]);
    } catch (error) {
      console.error("Error fetching departments list:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const [allEmployees, setAllEmployees] = useState([]);
  const getTeamByIdApiHandler = async (id) => {
    try {
      const response = await getTeamByIdApi(id);
      setTeamData(response.data || {});
      setAllEmployees(response.data?.matches || []);
      setEmployees(response.data?.matches || []);
      setEmployeesLength(response?.data?.matches?.length || null);
    } catch (error) {
      console.error("Error fetching team data:", error);
    }
  };

  // helper
  const safeIncludes = (val = "", term = "") =>
    String(val).toLowerCase().includes(term);

  // re-filter whenever inputs change
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const dept = (filterDepartment || "").trim(); // "" means all

    const filtered = allEmployees.filter((e) => {
      const matchesSearch =
        !term ||
        safeIncludes(e.name, term) ||
        safeIncludes(e.designation, term) || // ← use designation (what you render)
        safeIncludes(e.role, term) ||
        (Array.isArray(e.skills) &&
          e.skills.some((s) => safeIncludes(s, term)));

      const matchesDepartment =
        !dept || dept === "All" || e.department === dept;

      // const matchesSort =
      //   !sortBy || (sortBy === "name" && safeIncludes(e.name, term));

      return matchesSearch && matchesDepartment;
    });

    setEmployees(filtered);
    setEmployeesLength(filtered.length || null);
    setCurrentPage(1);
  }, [searchTerm, filterDepartment, allEmployees, sortBy]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // GET LEARNING PATH FOR SKILL
  const [showLearningPathModal, setShowLearningPathModal] = useState(false);
  const [mockLearningPath, setMockLearningPath] = useState([]);
  // Helper function to get level color
  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case "beginner":
        return "course-level-beginner";
      case "intermediate":
        return "course-level-intermediate";
      case "advanced":
        return "course-level-advanced";
      default:
        return "course-level-default";
    }
  };
  // Helper function to render star rating
  const renderStarRating = (rating) => {
    const numRating = Number.parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="course-rating-stars">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} size={12} className="star-filled" />
        ))}
        {hasHalfStar && <Star size={12} className="star-half" />}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} size={12} className="star-empty" />
        ))}
        <span className="rating-number">({numRating})</span>
      </div>
    );
  };

  const getLearningPathForSkillHandler = async (skillsOrSkill) => {
    try {
      setShowLearningPathModal(true);
      // Accept either an array of gaps or a single gap object
      const payload = Array.isArray(skillsOrSkill)
        ? skillsOrSkill
        : [skillsOrSkill];
      setMockLearningPath(payload);
    } catch (error) {
      console.error("Error fetching learning path:", error);
    }
  };

  // useEffect(() => {
  //   // Fetch employees from API
  //   getEmployeesDropdownApiHandler();
  // }, [currentPage, searchTerm, filterDepartment, filterAvailability, sortBy]);
  // const getEmployeesDropdownApiHandler = async () => {
  //   try {
  //     const obj = {
  //       pageNo: currentPage,
  //       pageSize: itemsPerPage,
  //       searchFilter: searchTerm,
  //       department: filterDepartment,
  //       availability: filterAvailability,
  //       sortBy: sortBy,
  //     };
  //     const response = await getAllPeopleApi(obj);
  //     setEmployees(response.data);
  //     setEmployeesLength(response.totalCount);
  //     setAvailableCandidates(response.data); // Update availableCandidates state
  //   } catch (error) {
  //     handleApiError(error, {
  //       showModal: false,
  //     });
  //   }
  // };

  // Helper to render skill sections with optional "more" button and modal
  // REPLACE your existing renderSkillSection with this version
  const renderSkillSection = ({
    title,
    skills,
    type,
    limit = 10,
    isEditable = false,
    isRemovable = false,
    valueMap, // <-- NEW: supply the exact value source for soft skills
    tooltip,
  }) => {
    // For soft skills, prefer the explicitly provided map; fall back to merged
    const usedValueMap = type === "soft" ? valueMap || softSkillValues : {};

    const displayedSkills = skills.slice(0, limit);
    const remainingCount = skills.length - limit;

    const openModal = () => {
      if (type === "soft") {
        const softSkillsWithValues = skills.map((skillName) => ({
          name: skillName,
          value:
            usedValueMap[skillName] !== undefined
              ? usedValueMap[skillName]
              : "N/A",
        }));
        setShowSoftSkillListModal({
          visible: true,
          title: `All ${title}`,
          skills: softSkillsWithValues,
          type: "soft",
        });
      } else {
        setShowJobSkillListModal({
          visible: true,
          title: `All ${title}`,
          skills: skills,
          type: "job",
          removable: isRemovable,
        });
      }
    };

    return (
      <div className="skill-section-container">
        <h4 style={{ display: "inline-flex", alignItems: "center" }}>
          {title} ({skills.length})
          <CustomTooltip
            tooltip={
              tooltip || {
                title: "Skill Info",
                message: "This section shows all the skills for this category.",
              }
            }
            id={`skills-tooltip-${title}`}
          >
            <Info
              size={14}
              color="#888"
              style={{
                marginLeft: "8px",
                cursor: "pointer",
                verticalAlign: "middle",
              }}
            />
          </CustomTooltip>
        </h4>
        <div
          className={type === "soft" ? "soft-skills-grid" : "job-skills-list"}
        >
          {displayedSkills.length === 0 ? (
            <span className="no-skills-message">No skills found.</span>
          ) : (
            displayedSkills.map((skill, index) => {
              if (type === "soft") {
                const skillName = skill;
                const value = usedValueMap[skillName];
                return (
                  <div key={index} className="soft-skill-item">
                    <div className="soft-skill-info">
                      <span className="soft-skill-name">{skillName}:</span>
                      {isEditable &&
                      editingSoftSkill?.skillName === skillName ? (
                        <div className="soft-skill-value-edit">
                          <input
                            type="number"
                            value={value}
                            onChange={(e) =>
                              handleSoftSkillValueChange(
                                skillName,
                                e.target.value,
                              )
                            }
                            onBlur={handleSaveSoftSkill}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveSoftSkill();
                              if (e.key === "Escape")
                                handleCancelEditSoftSkill();
                            }}
                          />
                          <div className="soft-skill-edit-actions">
                            <button
                              onClick={handleSaveSoftSkill}
                              className="soft-skill-save-btn"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEditSoftSkill}
                              className="soft-skill-cancel-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="soft-skill-display">
                          <span className="soft-skill-value">
                            {value !== undefined ? value : "N/A"}/10
                          </span>
                          {isEditable && (
                            <button
                              onClick={() =>
                                handleEditSoftSkill(skillName, value)
                              }
                              className="soft-skill-edit-btn"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={index} className="job-skill-card">
                    <span>{skill}</span>
                    {isRemovable && (
                      <button
                        onClick={() => handleRemoveJobSkill(skill)}
                        className="job-skill-remove-btn"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              }
            })
          )}
          {remainingCount > 0 && (
            <button onClick={openModal} className="view-more-skills-btn">
              +{remainingCount} more
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "info":
        return (
          <div className="team-tab-content">
            {/* Team Details Grid */}
            <div className="details-grid">
              <div className="detail-card">
                <h3>{t("teamDetailPage.teamSkills")}</h3>
                <div className="skills-tags">
                  {(teamData?.hardSkills?.skillsFound || [])
                    .slice(0, 20)
                    .map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {typeof skill === "string" ? skill : skill?.name}
                      </span>
                    ))}

                  {(teamData?.hardSkills?.skillsFound?.length || 0) > 20 && (
                    <button
                      className="view-more-skills-btn"
                      onClick={() =>
                        openViewMore(
                          t("teamDetailPage.teamSkills"),
                          teamData.hardSkills.skillsFound,
                        )
                      }
                    >
                      +{teamData.hardSkills.skillsFound.length - 20} more
                    </button>
                  )}
                </div>
              </div>
              <div className="detail-card">
                <h3>{t("teamDetailPage.currentProjects")}</h3>
                <div className="projects-list">
                  {teamData?.projects?.map((project, index) => (
                    <div key={index} className="project-item">
                      <Award size={14} />
                      <span>{project}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-card">
                <h3>{t("teamDetailPage.teamCulture")}</h3>
                {/* <p className="culture-text">
                  {(
                    (teamData?.topFiveSoftSkills ??
                      teamData?.topFiveSoftSkill) ||
                    []
                  ).map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </p> */}
                <div className="skills-tags">
                  {teamData?.topFiveSoftSkills?.map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* <div className="detail-card">
                <h3>{t("teamDetailPage.neededSkills")}</h3>
                <div className="needed-skills">
                  {teamData?.neededSkills?.map((skill, index) => (
                    <span key={index} className="needed-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div> */}
            </div>
          </div>
        );

      case "skills":
        // Prepare soft skills (objects → arrays of skill names)
        // required = benchmarkSkills, current = skillsFound, missing = skillsMissing
        const requiredSoftSkills = Object.keys(
          teamData?.softSkills?.benchmarkSkills || {},
        );
        const currentSoftSkills = Object.keys(
          teamData?.softSkills?.skillsFound || {},
        );
        const missingSoftSkills = Object.keys(
          teamData?.softSkills?.skillsMissing || {},
        );

        // Prepare job skills data for rendering
        const requiredJobSkills = teamData?.hardSkills?.skillsRequired || [];
        const currentJobSkills = teamData?.hardSkills?.skillsFound || [];
        const missingJobSkills = teamData?.hardSkills?.skillsMissing || [];

        return (
          <div className="team-tab-content">
            <div className="skills-analysis">
              {/* <h3>{t("teamDetailPage.skillGapsTraining")}</h3> */}

              {/* Soft Skills Section (table-based with derived values) */}
              <div className="skill-gaps-section">
                <h4>Soft Skills</h4>

                {(() => {
                  // 1) Required from benchmark
                  const softBenchmark =
                    teamData?.softSkills?.benchmarkSkills || {};

                  // 2) CURRENT = merged( skillsFound + skillsMissing )
                  //    The API's skillsMissing is actually another set of current values.
                  const currentMap = {
                    ...(teamData?.softSkills?.skillsFound || {}),
                    ...(teamData?.softSkills?.skillsMissing || {}),
                  };

                  // helpers
                  const toNum = (v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : 0;
                  };

                  const fmt = (n) => {
                    const rounded = Math.round(n * 100) / 100;
                    return rounded.toFixed(2).replace(/\.?0+$/, "");
                  };

                  // Only show the skills that have a Required benchmark (your “Required” column is the source of truth)
                  const softSkillNames = Object.keys(softBenchmark).sort();

                  const rows = softSkillNames.map((name) => {
                    const required = toNum(softBenchmark[name]); // out of 10
                    const current = toNum(currentMap[name]); // merged current value
                    const missing = Math.max(0, required - current); // clamp negatives to 0
                    return { name, required, current, missing };
                  });

                  return (
                    <div className="tm-soft-table-wrapper">
                      <div className="tm-soft-table-caption">
                        All values are out of 10
                      </div>
                      <table className="tm-soft-table" role="table">
                        <thead>
                          <tr>
                            <th>Soft Skill</th>
                            <th>
                              Required
                              <FloatingHeaderTooltip
                                tooltip={{
                                  title: t(
                                    "teamDetailPage.tooltips.requiredTitle",
                                  ),
                                  message: t(
                                    "teamDetailPage.tooltips.requiredMessage",
                                  ),
                                }}
                                id="required-tip"
                              >
                                <span className="tm-soft-info-icon">
                                  <Info size={14} color="#888" />
                                </span>
                              </FloatingHeaderTooltip>
                            </th>
                            <th>
                              Current
                              <FloatingHeaderTooltip
                                tooltip={{
                                  title: t(
                                    "teamDetailPage.tooltips.currentTitle",
                                  ),
                                  message: t(
                                    "teamDetailPage.tooltips.currentMessage",
                                  ),
                                }}
                                id="current-tip"
                              >
                                <span className="tm-soft-info-icon">
                                  <Info size={14} color="#888" />
                                </span>
                              </FloatingHeaderTooltip>
                            </th>
                            <th>
                              Missing
                              <FloatingHeaderTooltip
                                tooltip={{
                                  title: t(
                                    "teamDetailPage.tooltips.missingTitle",
                                  ),
                                  message: t(
                                    "teamDetailPage.tooltips.missingMessage",
                                  ),
                                }}
                                id="missing-tip"
                              >
                                <span className="tm-soft-info-icon">
                                  <Info size={14} color="#888" />
                                </span>
                              </FloatingHeaderTooltip>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="tm-soft-table-empty">
                                No soft skills available.
                              </td>
                            </tr>
                          ) : (
                            rows.map(({ name, required, current, missing }) => (
                              <tr key={name}>
                                <td className="tm-soft-name">{name}</td>
                                <td className="tm-soft-num">{fmt(required)}</td>
                                {/* 🔁 Correct column order: Current first, then Missing */}
                                <td className="tm-soft-num">{fmt(current)}</td>
                                <td className="tm-soft-num">{fmt(missing)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>

              {/* Job Skills Section */}
              <div className="skill-gaps-section">
                <h4>Job Skills</h4>
                {renderSkillSection({
                  title: "Required Job Skills",
                  skills: requiredJobSkills,
                  type: "job",
                  isRemovable: false,
                  tooltip: {
                    title: t("teamDetailPage.tooltips.requiredJobSkillsTitle"),
                    message: t(
                      "teamDetailPage.tooltips.requiredJobSkillsMessage",
                    ),
                  },
                })}
                {renderSkillSection({
                  title: "Current Job Skills",
                  skills: currentJobSkills,
                  type: "job",
                  isRemovable: false,
                  tooltip: {
                    title: t("teamDetailPage.tooltips.currentJobSkillsTitle"),
                    message: t(
                      "teamDetailPage.tooltips.currentJobSkillsMessage",
                    ),
                  },
                })}
                {renderSkillSection({
                  title: "Missing Job Skills",
                  skills: missingJobSkills,
                  type: "job",
                  isRemovable: false,
                  tooltip: {
                    title: t("teamDetailPage.tooltips.missingJobSkillsTitle"),
                    message: t(
                      "teamDetailPage.tooltips.missingJobSkillsMessage",
                    ),
                  },
                })}
              </div>
            </div>
          </div>
        );

      case "candidates":
        return (
          <div className="team-tab-content">
            {/* Current Team Members */}
            <div className="members-section">
              <h3>
                {t("teamDetailPage.currentTeamMembers", {
                  count: teamData?.members?.length,
                })}
              </h3>
              <div className="members-grid">
                {teamData?.members &&
                  teamData?.members?.map((member) => (
                    <div key={member._id} className="member-card-updated">
                      <div className="member-header">
                        <div className="member-info">
                          <div className="member-avatar">
                            <User size={20} />
                          </div>
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePersonClick(member)}
                          >
                            <h4>{member?.name}</h4>
                            <p className="member-role">{member?.designation}</p>
                            <p className="member-department">
                              {member?.department}
                            </p>
                          </div>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveMember(member)}
                          title={t("teamDetailPage.removeFromTeam")}
                        >
                          <Minus size={16} />
                        </button>
                      </div>

                      <div className="member-details">
                        <div className="detail-row">
                          <TrendingUp size={12} />
                          <span
                            className={`performance-text ${getPerformanceColor(
                              member?.teamFit,
                            )}`}
                            style={{ border: "none" }}
                          >
                            Team fit: {member?.teamFit}%
                          </span>
                          <div className="mobility-tooltip">
                            <Info
                              size={14}
                              className="mobility-score-info-icon"
                              aria-describedby={`exp-tip-${
                                member.userId || member.id || "x"
                              }`}
                              tabIndex={0}
                              onMouseEnter={decideTooltipSide}
                              onFocus={decideTooltipSide}
                            />
                            <div
                              className="mobility-score-hover-box"
                              role="tooltip"
                            >
                              <Explainability text={member.explainability} />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="member-skills">
                        <h5>{t("teamDetailPage.skills")}:</h5>
                        <div className="skills-tags">
                          {(member?.extractedHardSkills || [])
                            .slice(0, 5)
                            .map((skill, index) => (
                              <span key={index} className="skill-tag small">
                                {typeof skill === "string"
                                  ? skill
                                  : skill?.name}
                              </span>
                            ))}
                          {(member?.extractedHardSkills?.length || 0) > 5 && (
                            <button
                              className="view-more-skills-btn"
                              onClick={() =>
                                openViewMore(
                                  `All Skills — ${member?.name}`,
                                  member.extractedHardSkills,
                                )
                              }
                              style={{ fontSize: "12px" }}
                            >
                              +{member.extractedHardSkills.length - 5} more
                            </button>
                          )}
                        </div>

                        <h5 style={{ marginTop: "10px" }}>Skill gaps:</h5>
                        {Array.isArray(member?.skillGaps) &&
                        member.skillGaps.length > 0 ? (
                          <>
                            <div className="skills-tags">
                              {/* {member.skillGaps
                                .slice(0, 5)
                                .map((gap, index) => (
                                  <span key={index} className="skill-tag small">
                                    {gap?.skill_gap || gap?.name || gap}
                                  </span>
                                ))} */}
                              {member.skillGaps
                                .slice(0, 5)
                                .map((gap, index) => (
                                  <span key={index} className="skill-tag small">
                                    {getSkillGapLabel(gap)}
                                  </span>
                                ))}
                              {member.skillGaps.length > 5 && (
                                <button
                                  className="view-more-skills-btn"
                                  onClick={() =>
                                    openViewMore(
                                      `All Skill Gaps — ${member?.name}`,
                                      member.skillGaps,
                                    )
                                  }
                                  style={{ fontSize: "12px" }}
                                >
                                  +{member.skillGaps.length - 5} more
                                </button>
                              )}
                            </div>

                            {/* Show learning path button ONLY when there are gaps */}
                            <button
                              className="btn btn-primary card-fixed-cta"
                              style={{ marginTop: "15px" }}
                              onClick={() =>
                                getLearningPathForSkillHandler(member.skillGaps)
                              }
                            >
                              {t("upskillPage.assignTraining")}
                            </button>
                          </>
                        ) : (
                          // No gaps → show message and hide the button
                          <div className="skills-tags">
                            <span
                              style={{
                                opacity: 0.8,
                                fontSize: "12px",
                                fontStyle: "italic",
                              }}
                            >
                              {t?.("teamDetailPage.noSkillGaps") ||
                                "No skill gaps"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Add Members Button */}
            {/* <div className="add-members-section">
              <button
                className="btn btn-primary add-members-toggle"
                onClick={() => {
                  getEmployeesDropdownApiHandler();
                  setShowAddMembers(!showAddMembers);
                }}
              >
                <UserPlus size={16} />
                {showAddMembers
                  ? t("teamDetailPage.hideAvailableCandidates")
                  : t("teamDetailPage.addTeamMembers")}
              </button>
            </div> */}

            {/* Available Candidates - Only show when button is clicked */}

            <div className="candidates-section">
              <h3>Available Recommended members</h3>

              <div className="usearch-filters">
                <div className="usearch-filters-row">
                  <div className="usearch-container">
                    <Search size={20} className="usearch-icon" />
                    <input
                      type="text"
                      placeholder={t("teamDetailPage.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>

                  <div className="ufilter-group">
                    <Filter size={16} />
                    <label>{t("teamDetailPage.filters.department")}</label>
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="ufilter-select"
                    >
                      {departmentsList.map((dept) => (
                        <option key={dept._id} value={dept.name}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* <div className="ufilter-group">
                    <Filter size={16} />
                    <label>{t("teamDetailPage.filters.availability")}:</label>
                    <select
                      value={filterAvailability}
                      onChange={(e) => setFilterAvailability(e.target.value)}
                      className="ufilter-select"
                    >
                      {availabilityOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  {/* <div className="ufilter-group">
                    <SortAsc size={16} />
                    <label>{t("teamDetailPage.filters.sortBy")}:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="ufilter-select"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div> */}
                </div>
              </div>

              {paginatedEmployees.length === 0 ? (
                <NoDataFound />
              ) : (
                <div className="candidates-scroll">
                  <div className="candidates-grid">
                    {paginatedEmployees.map((candidate) => (
                      <div key={candidate.id} className="candidate-card">
                        <div className="ccandidate-header">
                          <div
                            style={{ cursor: "pointer" }}
                            className="ccandidate-info"
                            onClick={() => handlePersonClick(candidate)}
                          >
                            <div className="ccandidate-avatar">
                              <User size={20} />
                            </div>
                            <div>
                              <h4>{candidate.name}</h4>
                              <p className="ccandidate-role">
                                {candidate.designation}
                              </p>
                              <p className="ccandidate-department">
                                {candidate.department}
                              </p>
                            </div>
                          </div>
                          <button
                            className="add-btn"
                            onClick={() => handleAddMember(candidate)}
                            title={t("teamDetailPage.addToTeam")}
                          >
                            <Plus size={16} />
                          </button>
                        </div>

                        <div className="candidate-details">
                          {/* <div className="detail-row"> */}
                          {/* <span
                          className={`availability-badge ${getAvailabilityColor(
                            candidate.availability
                          )}`}
                        >
                          {candidate.availability}
                        </span> */}
                          {/* </div> */}
                          <div
                            className="detail-row"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              // justifyContent:"space-between"
                            }}
                          >
                            <span
                              className={`fit-score ${getFitColor(
                                candidate.teamFit,
                              )}`}
                            >
                              {t("teamDetailPage.teamFit")}: {candidate.teamFit}
                              %
                            </span>

                            <div className="mobility-tooltip">
                              <Info
                                size={14}
                                className="mobility-score-info-icon"
                                aria-describedby={`exp-tip-${
                                  candidate.userId || candidate.id || "x"
                                }`}
                                tabIndex={0}
                                onMouseEnter={decideTooltipSide}
                                onFocus={decideTooltipSide}
                              />
                              <div
                                className="mobility-score-hover-box"
                                role="tooltip"
                              >
                                <Explainability
                                  text={candidate.explainability}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="candidate-skills">
                          <h5>{t("teamDetailPage.skills")}:</h5>
                          <div className="skills-tags">
                            {(candidate.skills || [])
                              .slice(0, 5)
                              .map((skill, index) => (
                                <span key={index} className="skill-tag small">
                                  {typeof skill === "string"
                                    ? skill
                                    : skill?.name}
                                </span>
                              ))}
                            {(candidate.skills?.length || 0) > 5 && (
                              <button
                                className="view-more-skills-btn"
                                onClick={() =>
                                  openViewMore(
                                    `All Skills — ${candidate?.name}`,
                                    candidate.skills,
                                  )
                                }
                                style={{ fontSize: "12px" }}
                              >
                                +{candidate.skills.length - 5} more
                              </button>
                            )}
                          </div>

                          {/* If candidate has skill gaps, show first 5 + popup */}
                          {Array.isArray(candidate?.skillGaps) &&
                            candidate.skillGaps.length > 0 && (
                              <>
                                <h5 style={{ marginTop: "10px" }}>
                                  Skill gaps:
                                </h5>
                                <div className="skills-tags">
                                  {/* {candidate.skillGaps
                                    .slice(0, 5)
                                    .map((gap, i) => (
                                      <span key={i} className="skill-tag small">
                                        {gap?.skill_gap || gap?.name || gap}
                                      </span>
                                    ))} */}
                                  {candidate.skillGaps
                                    .slice(0, 5)
                                    .map((gap, index) => (
                                      <span
                                        key={index}
                                        className="skill-tag small"
                                      >
                                        {getSkillGapLabel(gap)}
                                      </span>
                                    ))}{" "}
                                  {candidate.skillGaps.length > 5 && (
                                    <button
                                      className="view-more-skills-btn"
                                      onClick={() =>
                                        openViewMore(
                                          `All Skill Gaps — ${candidate?.name}`,
                                          candidate.skillGaps,
                                        )
                                      }
                                    >
                                      +{candidate.skillGaps.length - 5} more
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {employeesLength && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    {t("teamDetailPage.pagination.showing")}{" "}
                    {indexOfFirstItem + 1} {t("teamDetailPage.pagination.to")}{" "}
                    {Math.min(indexOfLastItem, employees.length)}{" "}
                    {t("teamDetailPage.pagination.of")} {employeesLength}{" "}
                    {t("teamDetailPage.pagination.employees")}
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (pageNumber) => (
                        <button
                          key={pageNumber}
                          className={`pagination-btn ${
                            currentPage === pageNumber ? "active" : ""
                          }`}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      ),
                    )}
                    <button
                      className="pagination-btn"
                      onClick={handleNextPage}
                      disabled={currentPage === employeesLength}
                    >
                      <ChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="team-detail-page">
      <button
        className="nav-back"
        onClick={() => window.history.back()}
        style={{
          position: "absolute",
          top: "100px",
          left: "20px",
          color: "#333",
        }}
      >
        <ArrowLeft size={16} />
        {t("common.back")}
      </button>

      <main className="team-detail-main">
        <div className="container">
          <h1
            style={{
              textAlign: "center",
              fontWeight: "600",
              marginBottom: "10px",
            }}
          >
            {t("teamDetailPage.pageTitle")}
          </h1>
          <ProfileCard
            icon={
              <span className="team-detail-centered-main-icon">
                <Users size={48} />
              </span>
            }
            safeCandidate={teamData}
            context="team"
            candidateStats={{
              upskill: {
                missingSkills: teamData?.upskillNeeded,
              },
              leadership: {
                readiness: teamData.leadershipCandidates,
              },
              mobility: {
                opportunities: teamData?.mobilityOpportunities,
              },
            }}
            onSkillsToDevelopClick={() => setActiveTab("candidates")}
            onNavigate={onNavigate}
            performance={teamData?.performance}
            openPositions={teamData?.openPositions}
          />

          <div className="profile-edit-section-unique">
            <button
              className="btn btn-primary profile-edit-btn-unique"
              onClick={handleEditTeam}
            >
              <Plus size={16} />
              {t("teamDetailPage.editTeamDetails")}
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="team-tab-navigation" id="add-members-tab">
            <button
              className={`team-tab-btn ${activeTab === "info" ? "active" : ""}`}
              onClick={() => setActiveTab("info")}
            >
              <Settings size={16} />
              {t("teamDetailPage.tabs.teamInfo")}
            </button>
            <button
              className={`team-tab-btn ${
                activeTab === "skills" ? "active" : ""
              }`}
              onClick={() => setActiveTab("skills")}
            >
              <Award size={16} />
              {t("teamDetailPage.tabs.skillsTraining")}
            </button>
            <button
              className={`team-tab-btn ${
                activeTab === "candidates" ? "active" : ""
              }`}
              onClick={() => setActiveTab("candidates")}
            >
              <UserPlus size={16} />
              {t("teamDetailPage.tabs.teamMembers")}
            </button>
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </main>

      {/* Soft Skills List Modal */}
      {showSoftSkillListModal.visible && (
        <SkillListModal
          title={showSoftSkillListModal.title}
          skills={showSoftSkillListModal.skills}
          type="soft"
          onClose={() =>
            setShowSoftSkillListModal({
              visible: false,
              title: "",
              skills: [],
              type: "soft",
            })
          }
        />
      )}

      {/* Job Skills List Modal */}
      {showJobSkillListModal.visible && (
        <SkillListModal
          title={showJobSkillListModal.title}
          skills={showJobSkillListModal.skills}
          type="job"
          onClose={() =>
            setShowJobSkillListModal({
              visible: false,
              title: "",
              skills: [],
              type: "job",
              removable: false,
            })
          }
          onRemoveSkill={
            showJobSkillListModal.removable ? handleRemoveJobSkill : undefined
          }
        />
      )}

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            action: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      >
        {showModal.showLocalButton && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  action: null,
                })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("common.confirm")}
            </button>
          </div>
        )}
      </Modal>

      {/* Learning Path Modal */}
      {showLearningPathModal && (
        <div className="learning-path-modal-overlay">
          <div className="learning-path-modal">
            <div className="learning-path-modal-header">
              <div className="modal-header-content">
                <div className="modal-title-section">
                  <div className="upskill-modal-icon">
                    <Target size={24} />
                  </div>
                  <div>
                    <h2>{t("upskillPage.learningPathModal.title")}</h2>
                    {/* <p>
                      {t("upskillPage.learningPathModal.description", {
                        name: selectedPerson.name,
                      })}
                    </p> */}
                  </div>
                </div>
                <div className="modal-actions">
                  {/* <button
                    className="download-btn"
                    onClick={handleDownloadPDF}
                    title={t("upskillPage.learningPathModal.downloadPdf")}
                  >
                    <Download size={18} />
                    {t("upskillPage.learningPathModal.downloadPdf")}
                  </button> */}
                  <button
                    className="close-btn"
                    onClick={() => setShowLearningPathModal(false)}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>

            <div className="learning-path-content">
              <div className="skills-recommendations">
                {Array.isArray(mockLearningPath) &&
                mockLearningPath.length > 0 ? (
                  mockLearningPath.map((gap, idx) => (
                    <div key={idx} className="skill-recommendation-card">
                      <div className="skill-header">
                        <div className="skill-info">
                          {/* <h3>{gap.skill_gap}</h3> */}
                          <h3>{getSkillGapLabel(gap)}</h3>
                          <div className="skill-meta">
                            <span
                              className={`skill-type-badge ${
                                gap.type === "Job Skill"
                                  ? "job-skill"
                                  : "soft-skill"
                              }`}
                            >
                              {gap.type === "Job Skill" ? (
                                <Briefcase size={14} />
                              ) : (
                                <User size={14} />
                              )}
                              {gap.type}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Recommended Courses */}
                      {Array.isArray(gap.recommended_courses) &&
                        gap.recommended_courses.length > 0 && (
                          <div className="courses-section">
                            <h4>
                              <BookOpen size={16} />
                              {t("upskillPage.recommendedCourses")}
                            </h4>
                            <div className="courses-grid-improved">
                              {gap.recommended_courses.map(
                                (course, courseIndex) => (
                                  <div
                                    key={courseIndex}
                                    className="course-card-improved"
                                  >
                                    <div className="course-card-header">
                                      <div className="course-icon">
                                        <PlayCircle size={20} />
                                      </div>
                                      <div className="course-level-badge">
                                        <Award size={12} />
                                        <span
                                          className={`level-text ${getLevelColor(
                                            course.level,
                                          )}`}
                                        >
                                          {course.level || "Beginner"}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="course-content">
                                      <h5 className="course-title">
                                        {course.course_name}
                                      </h5>
                                      <div className="course-rating">
                                        {renderStarRating(course.rating)}
                                      </div>
                                    </div>

                                    <div
                                      className="course-footer"
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        paddingTop: "16px",
                                        borderTop: "1px solid #e5e7eb",
                                      }}
                                    >
                                      <span
                                        className="course-platform-tag"
                                        style={{
                                          fontSize: "12px",
                                          color: "var(--text-secondary)",
                                          background: "#f1f5f9",
                                          padding: "4px 8px",
                                          borderRadius: "8px",
                                        }}
                                      >
                                        {course.platform ||
                                          t(
                                            "upskillPage.trainingModal.platformPlaceholder",
                                          )}
                                      </span>
                                      <a
                                        href={course.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="course-link-btn"
                                      >
                                        <ExternalLink size={14} />
                                        {t("common.view")}
                                      </a>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {/* Optional: Steps / Path */}
                      {Array.isArray(gap.steps) && gap.steps.length > 0 && (
                        <div
                          className="steps-section"
                          style={{ marginTop: 16 }}
                        >
                          <h4>
                            <Target size={16} />
                            {t("upskillPage.learningPathModal.title")}
                          </h4>
                          <ol className="steps-timeline">
                            {gap.steps.map((s, i) => (
                              <div key={i} className="step-item">
                                <div className="step-indicator">
                                  <div className="step-number">{i + 1}</div>
                                  <div className="step-stage">{s.stage}</div>
                                </div>
                                <div className="step-content">
                                  <p>{s.description}</p>
                                </div>
                              </div>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-skills-message">No skill gaps found.</div>
                )}
              </div>
            </div>

            <div className="learning-path-footer">
              <div className="footer-note">
                <Lightbulb size={16} />
                <span>{t("upskillPage.learningPathModal.noteMessage")}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <ViewMoreModal
        open={vmOpen}
        onClose={() => setVmOpen(false)}
        title={vmTitle}
        skills={vmSkills}
        variant="job-skills" // uses your modal CSS/chips
        type="hard" // treat as simple list; 'soft' shows values if present
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default TeamDetailPage;
